import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import { api } from '../App';
import { Lock, Unlock, Filter, TrendingUp, TrendingDown, Calendar, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const fmtINR = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return sign + '₹' + Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};
const fmtQty = (n) => (n || 0).toLocaleString('en-IN');
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d.getDate()}${months[d.getMonth()]}${d.getFullYear()}`;
  } catch { return iso; }
};

const defaultFrom = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().slice(0, 10);
};
const todayStr = () => new Date().toISOString().slice(0, 10);

const PledgeFlow = ({ user, setUser, embedded = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [filters, setFilters] = useState({
    from_date: defaultFrom(), to_date: todayStr(),
    member_id: '', symbol: '',
  });
  const [groupBy, setGroupBy] = useState('bank');

  const fetchMembers = useCallback(async () => {
    try {
      const res = await api.get('/family-members');
      setMembers(res.data || []);
    } catch (e) { /* non-fatal */ }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      if (filters.member_id) params.member_id = filters.member_id;
      if (filters.symbol.trim()) params.symbol = filters.symbol.trim();
      const res = await api.get('/analytics/pledge-flow', { params });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load pledge flow');
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const groupData = useMemo(() => {
    if (!data) return [];
    if (groupBy === 'bank') return data.by_bank;
    if (groupBy === 'symbol') return data.by_symbol;
    return data.by_member;
  }, [data, groupBy]);

  const totals = data?.totals;

  const content = (
      <div data-testid="pledge-flow-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight mb-1 flex items-center gap-2">
            <BarChart3 className="text-indigo-600" size={28} /> Pledge Flow Report
          </h1>
          <p className="text-sm text-gray-600">Chronological record of pledge and unpledge movements across banks and stocks.</p>
        </div>

        {/* Filters */}
        <div className="card p-4 mb-5" data-testid="filters-card">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={16} className="text-gray-500" />
            <h3 className="font-semibold text-gray-700">Filters</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">From Date</label>
              <input
                type="date" value={filters.from_date}
                onChange={e => setFilters({ ...filters, from_date: e.target.value })}
                className="input-field w-full text-sm" data-testid="from-date"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">To Date</label>
              <input
                type="date" value={filters.to_date}
                onChange={e => setFilters({ ...filters, to_date: e.target.value })}
                className="input-field w-full text-sm" data-testid="to-date"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Member</label>
              <select
                value={filters.member_id}
                onChange={e => setFilters({ ...filters, member_id: e.target.value })}
                className="input-field w-full text-sm" data-testid="filter-member"
              >
                <option value="">All Members</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Symbol</label>
              <input
                type="text" value={filters.symbol}
                onChange={e => setFilters({ ...filters, symbol: e.target.value })}
                placeholder="e.g. ICICIBANK" className="input-field w-full text-sm"
                data-testid="filter-symbol"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ from_date: defaultFrom(), to_date: todayStr(), member_id: '', symbol: '' })}
                className="text-xs text-gray-500 hover:text-indigo-600"
                data-testid="reset-filters-btn"
              >
                Reset filters
              </button>
            </div>
          </div>
        </div>

        {/* Summary cards */}
        {totals && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="summary-cards">
            <div className="card p-3 bg-gradient-to-br from-red-50 to-rose-50">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Pledged</p>
                <Lock size={14} className="text-red-500" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-red-700">{fmtINR(totals.pledged_value)}</p>
              <p className="text-[11px] text-gray-500 mt-1">{fmtQty(totals.pledged_qty)} shares · {totals.pledge_events} events</p>
            </div>
            <div className="card p-3 bg-gradient-to-br from-emerald-50 to-green-50">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Unpledged</p>
                <Unlock size={14} className="text-emerald-500" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-emerald-700">{fmtINR(totals.unpledged_value)}</p>
              <p className="text-[11px] text-gray-500 mt-1">{fmtQty(totals.unpledged_qty)} shares · {totals.unpledge_events} events</p>
            </div>
            <div className="card p-3 bg-gradient-to-br from-indigo-50 to-blue-50">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Net Movement</p>
                {totals.net_value >= 0 ? <TrendingUp size={14} className="text-indigo-500" /> : <TrendingDown size={14} className="text-indigo-500" />}
              </div>
              <p className={`text-xl sm:text-2xl font-bold ${totals.net_value >= 0 ? 'text-indigo-700' : 'text-amber-700'}`}>
                {totals.net_value >= 0 ? '+' : ''}{fmtINR(totals.net_value)}
              </p>
              <p className="text-[11px] text-gray-500 mt-1">Net qty: {totals.net_qty >= 0 ? '+' : ''}{fmtQty(totals.net_qty)}</p>
            </div>
            <div className="card p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Total Events</p>
                <Calendar size={14} className="text-gray-500" />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-gray-700">{totals.events}</p>
              <p className="text-[11px] text-gray-500 mt-1">in selected range</p>
            </div>
          </div>
        )}

        {loading && <p className="text-sm text-gray-500 my-2">Loading…</p>}

        {/* Grouped breakdown */}
        {data && totals && totals.events > 0 && (
          <div className="card mb-5" data-testid="group-breakdown">
            <div className="p-3 border-b flex items-center justify-between gap-2 flex-wrap">
              <h3 className="font-semibold text-gray-700">Aggregated by</h3>
              <div className="flex gap-1 text-xs" data-testid="group-toggle">
                {['bank', 'symbol', 'member'].map(k => (
                  <button
                    key={k}
                    onClick={() => setGroupBy(k)}
                    className={`px-2.5 py-1 rounded font-medium ${groupBy === k ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    data-testid={`group-by-${k}`}
                  >
                    {k[0].toUpperCase() + k.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold">
                      {groupBy === 'bank' ? 'Bank' : groupBy === 'symbol' ? 'Symbol' : 'Member'}
                    </th>
                    <th className="text-right py-2 px-3 font-semibold">Pledge Qty</th>
                    <th className="text-right py-2 px-3 font-semibold">Pledge Value</th>
                    <th className="text-right py-2 px-3 font-semibold">Unpledge Qty</th>
                    <th className="text-right py-2 px-3 font-semibold">Unpledge Value</th>
                    <th className="text-right py-2 px-3 font-semibold">Net</th>
                    <th className="text-right py-2 px-3 font-semibold">Events</th>
                  </tr>
                </thead>
                <tbody>
                  {groupData.map((row, i) => {
                    const label = row.bank || row.symbol || row.member_name;
                    return (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-3 font-medium">{label}</td>
                        <td className="py-2 px-3 text-right text-red-700">{fmtQty(row.pledge_qty)}</td>
                        <td className="py-2 px-3 text-right text-red-700">{fmtINR(row.pledge_value)}</td>
                        <td className="py-2 px-3 text-right text-emerald-700">{fmtQty(row.unpledge_qty)}</td>
                        <td className="py-2 px-3 text-right text-emerald-700">{fmtINR(row.unpledge_value)}</td>
                        <td className={`py-2 px-3 text-right font-bold ${row.net_value >= 0 ? 'text-indigo-700' : 'text-amber-700'}`}>
                          {row.net_value >= 0 ? '+' : ''}{fmtINR(row.net_value)}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-500">{row.events}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Chronological events */}
        <div className="card" data-testid="events-card">
          <div className="p-3 border-b">
            <h3 className="font-semibold text-gray-700">Events ({data?.events?.length || 0})</h3>
          </div>
          {(!data || !data.events || data.events.length === 0) ? (
            <div className="text-center py-12 text-gray-500">
              <Lock size={32} className="mx-auto mb-2 opacity-40" />
              <p>No pledge/unpledge events in this period.</p>
              <p className="text-xs mt-1">Events are recorded when you pledge or unpledge holdings from "Know your Stock".</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold">Date</th>
                    <th className="text-left py-2 px-3 font-semibold">Type</th>
                    <th className="text-left py-2 px-3 font-semibold">Member</th>
                    <th className="text-left py-2 px-3 font-semibold">Symbol</th>
                    <th className="text-right py-2 px-3 font-semibold">Qty</th>
                    <th className="text-right py-2 px-3 font-semibold">Price</th>
                    <th className="text-right py-2 px-3 font-semibold">Value</th>
                    <th className="text-left py-2 px-3 font-semibold">Bank</th>
                    <th className="text-left py-2 px-3 font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {data.events.map(ev => (
                    <tr key={ev.id} className="border-b hover:bg-gray-50" data-testid={`event-${ev.id}`}>
                      <td className="py-2 px-3 text-gray-700">{fmtDate(ev.datetime)}</td>
                      <td className="py-2 px-3">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex items-center gap-1 w-fit ${ev.type === 'pledge' ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}`}>
                          {ev.type === 'pledge' ? <Lock size={10} /> : <Unlock size={10} />} {ev.type}
                        </span>
                      </td>
                      <td className="py-2 px-3">{ev.member_name}</td>
                      <td className="py-2 px-3 font-medium">{ev.symbol}</td>
                      <td className="py-2 px-3 text-right">{fmtQty(ev.quantity)}</td>
                      <td className="py-2 px-3 text-right">₹{ev.price.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                      <td className={`py-2 px-3 text-right font-semibold ${ev.type === 'pledge' ? 'text-red-700' : 'text-emerald-700'}`}>
                        {fmtINR(ev.value)}
                      </td>
                      <td className="py-2 px-3 text-gray-600">{ev.bank}</td>
                      <td className="py-2 px-3 text-xs text-gray-500 max-w-xs truncate" title={ev.notes}>{ev.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
  );

  if (embedded) return content;
  return <Layout user={user} setUser={setUser}>{content}</Layout>;
};

export default PledgeFlow;
